import { useState, useEffect } from "react";
import axios from "axios";
import { X, Save, Calendar, Star, Trash2, AlertCircle, Sparkles, Wand2, Loader2 } from "lucide-react";
import ImageField from "@/components/admin/ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const toLocalDateTimeInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

const fromLocalDateTimeInput = (val) => {
  if (!val) return null;
  return new Date(val).toISOString();
};

const decodeEntities = (s = "") =>
  s.replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘").replace(/&#038;/g, "&").replace(/&amp;/g, "&")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—").replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

export default function DevotionalEditor({ post, categories, tags, token, onClose, onSaved }) {
  const isNew = !post?.id && !post?.slug;
  const [form, setForm] = useState({
    title: decodeEntities(post?.title || ""),
    slug: post?.slug || "",
    content_html: post?.content_html || "",
    excerpt: post?.excerpt || "",
    status: post?.status || "draft",
    published_at_local: toLocalDateTimeInput(post?.published_at) || toLocalDateTimeInput(new Date().toISOString()),
    categories: post?.categories || [],
    tags: post?.tags || [],
    featured_image_url: post?.featured_image_url || "",
    is_featured: !!post?.is_featured,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  // AI assist state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [seoBusy, setSeoBusy] = useState(false);
  const [seoSuggestedTags, setSeoSuggestedTags] = useState([]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleArr = (k, val) =>
    setForm((s) => {
      const arr = new Set(s[k] || []);
      if (arr.has(val)) arr.delete(val); else arr.add(val);
      return { ...s, [k]: Array.from(arr) };
    });

  // Map AI-suggested tag names/phrases to existing tag slugs (case-insensitive on slug or name)
  const matchTagsToExisting = (aiTags = []) => {
    const matched = [];
    const unmatched = [];
    const norm = (s) => String(s || "").toLowerCase().trim().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, "");
    aiTags.forEach((raw) => {
      const target = norm(raw);
      const hit = tags?.find((t) => norm(t.slug) === target || norm(t.name) === target);
      if (hit) matched.push(hit.slug);
      else unmatched.push(String(raw));
    });
    return { matched, unmatched };
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    setAiError("");
    try {
      const r = await axios.post(
        `${API}/admin/ai/devotional`,
        { topic_or_verse: aiPrompt.trim() },
        { headers: { "X-Admin-Token": token }, timeout: 90000 }
      );
      const d = r.data || {};
      const scriptureBlock = d.scripture_text
        ? `<blockquote><p><em>${d.scripture_text}</em></p><p><strong>— ${d.scripture_ref || ""}</strong></p></blockquote>\n`
        : "";
      const prayerBlock = d.prayer
        ? `\n<h3>Prayer</h3>\n<p><em>${d.prayer}</em></p>`
        : "";
      const body = `${scriptureBlock}${d.content_html || ""}${prayerBlock}`;
      const { matched, unmatched } = matchTagsToExisting(d.tags || []);
      setForm((s) => ({
        ...s,
        title: d.title || s.title,
        slug: s.slug || d.slug || "",
        excerpt: d.excerpt || s.excerpt,
        content_html: body || s.content_html,
        tags: Array.from(new Set([...(s.tags || []), ...matched])),
      }));
      setSeoSuggestedTags(unmatched);
      setAiOpen(false);
      setAiPrompt("");
    } catch (e) {
      setAiError(e.response?.data?.detail || e.message || "Claude request failed.");
    } finally {
      setAiBusy(false);
    }
  };

  const aiAutofillSEO = async () => {
    if (!form.title.trim() || !form.content_html.trim()) {
      setAiError("Add a title and content first, then auto-fill SEO.");
      return;
    }
    setSeoBusy(true);
    setAiError("");
    try {
      const r = await axios.post(
        `${API}/admin/ai/seo`,
        { title: form.title, content_html: form.content_html },
        { headers: { "X-Admin-Token": token }, timeout: 60000 }
      );
      const d = r.data || {};
      const { matched, unmatched } = matchTagsToExisting(d.tags || []);
      setForm((s) => ({
        ...s,
        excerpt: d.excerpt || s.excerpt,
        tags: Array.from(new Set([...(s.tags || []), ...matched])),
      }));
      setSeoSuggestedTags(unmatched);
    } catch (e) {
      setAiError(e.response?.data?.detail || e.message || "SEO assist failed.");
    } finally {
      setSeoBusy(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSaving(true);
    const headers = { "X-Admin-Token": token };
    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      content_html: form.content_html,
      excerpt: form.excerpt,
      status: form.status,
      published_at: fromLocalDateTimeInput(form.published_at_local),
      categories: form.categories,
      tags: form.tags,
      featured_image_url: form.featured_image_url || null,
      is_featured: form.is_featured,
    };
    try {
      if (isNew) {
        const r = await axios.post(`${API}/admin/posts`, payload, { headers });
        onSaved?.(r.data, "created");
      } else {
        const id = post.id || post.slug;
        const r = await axios.put(`${API}/admin/posts/${id}`, payload, { headers });
        onSaved?.(r.data, "updated");
      }
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setDeleting(true);
    setError("");
    try {
      const id = post.id || post.slug;
      await axios.delete(`${API}/admin/posts/${id}`, { headers: { "X-Admin-Token": token } });
      onSaved?.(post, "deleted");
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="devotional-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">
              {isNew ? "New Devotional" : "Edit Devotional"}
            </div>
            <h2 className="font-display text-2xl text-navy-deep mt-0.5">
              {form.title || "Untitled"}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="editor-close" className="w-10 h-10 rounded-full hover:bg-cream-100 flex items-center justify-center">
            <X size={20} className="text-navy" />
          </button>
        </div>

        <form onSubmit={save} className="px-6 py-6 grid lg:grid-cols-3 gap-6">
          {/* AI ASSIST BAR */}
          <div className="lg:col-span-3 rounded-xl border border-gold/40 bg-gradient-to-r from-cream-50 to-white p-4" data-testid="ai-assist-bar">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
                  <Sparkles size={16} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Claude AI Assistant</div>
                  <p className="text-sm text-navy-deep">Draft a devotional from a verse or topic, or auto-fill SEO from your content.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  data-testid="ai-toggle-generate"
                  onClick={() => { setAiOpen((v) => !v); setAiError(""); }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy-deep text-cream text-sm font-medium hover:opacity-90"
                >
                  <Sparkles size={14} /> Generate with Claude
                </button>
                <button
                  type="button"
                  data-testid="ai-seo-autofill"
                  onClick={aiAutofillSEO}
                  disabled={seoBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold text-[var(--gold-600)] bg-white text-sm font-medium hover:bg-gold/10 disabled:opacity-50"
                >
                  {seoBusy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  Auto-fill SEO
                </button>
              </div>
            </div>
            {aiOpen && (
              <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="ai-generate-row">
                <input
                  data-testid="ai-prompt-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aiGenerate(); } }}
                  placeholder="e.g. John 15:5 — abiding in Christ"
                  className="flex-1 min-w-[260px] px-4 py-2.5 rounded-xl border border-cream-200 bg-white focus-gold focus:outline-none"
                  autoFocus
                />
                <button
                  type="button"
                  data-testid="ai-generate-btn"
                  onClick={aiGenerate}
                  disabled={aiBusy || !aiPrompt.trim()}
                  className="btn-primary px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {aiBusy ? "Generating…" : "Generate"}
                </button>
                <button type="button" onClick={() => setAiOpen(false)} className="text-sm text-navy/60 hover:text-navy-deep px-3 py-2">Cancel</button>
              </div>
            )}
            {aiError && (
              <p data-testid="ai-error" className="mt-2 text-sm text-red-700 inline-flex items-center gap-1.5">
                <AlertCircle size={14} /> {aiError}
              </p>
            )}
            {seoSuggestedTags.length > 0 && (
              <div className="mt-3" data-testid="ai-suggested-tags">
                <div className="text-xs uppercase tracking-wider text-navy/60 mb-1.5">Suggested tags (not yet in your library)</div>
                <div className="flex flex-wrap gap-1.5">
                  {seoSuggestedTags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-full text-xs bg-cream-100 text-navy-deep border border-cream-200">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* MAIN COLUMN */}
          <div className="lg:col-span-2 space-y-4">
            <Field label="Title" required>
              <input
                data-testid="editor-title"
                required
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none"
                placeholder="Devotional title"
              />
            </Field>
            <Field label="Slug" hint="URL fragment — auto-generated if blank. e.g. 'morning-mercies'">
              <input
                data-testid="editor-slug"
                value={form.slug}
                onChange={(e) => update("slug", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none font-mono text-sm"
                placeholder="auto-generated-from-title"
              />
            </Field>
            <Field label="Excerpt" hint="1–2 sentences shown on cards & feeds.">
              <textarea
                data-testid="editor-excerpt"
                rows={2}
                value={form.excerpt}
                onChange={(e) => update("excerpt", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none resize-y"
              />
            </Field>
            <Field label="Content (HTML)" hint="Supports HTML. Headings, paragraphs, blockquotes, images, links.">
              <textarea
                data-testid="editor-content"
                rows={14}
                value={form.content_html}
                onChange={(e) => update("content_html", e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none resize-y font-mono text-sm leading-relaxed"
                placeholder='<p>Today, the Word of the Lord says...</p>\n<blockquote>"I am the vine..."<br/>— John 15:5</blockquote>'
              />
            </Field>
            {form.content_html && (
              <details className="rounded-xl border border-cream-200 bg-cream-50">
                <summary className="px-4 py-3 cursor-pointer text-sm font-semibold text-navy">Preview</summary>
                <div className="p-5 wp-content bg-white rounded-b-xl border-t border-cream-200" dangerouslySetInnerHTML={{ __html: form.content_html }} />
              </details>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5">
            {/* Publish controls */}
            <div className="rounded-xl border border-cream-200 p-5 bg-cream-50">
              <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">Publish</div>
              <Field label="Status">
                <select
                  data-testid="editor-status"
                  value={form.status}
                  onChange={(e) => update("status", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-cream-200 bg-white focus-gold focus:outline-none"
                >
                  <option value="draft">Draft</option>
                  <option value="publish">Published / Scheduled</option>
                </select>
              </Field>
              <Field label="Publish date & time" hint={
                form.status === "publish" && form.published_at_local && new Date(form.published_at_local) > new Date()
                  ? "Future date — will auto-publish at this moment."
                  : ""
              }>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
                  <input
                    data-testid="editor-publish-at"
                    type="datetime-local"
                    value={form.published_at_local}
                    onChange={(e) => update("published_at_local", e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-200 bg-white focus-gold focus:outline-none"
                  />
                </div>
              </Field>
              <label className="flex items-center gap-3 cursor-pointer mt-2">
                <input
                  data-testid="editor-featured"
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={(e) => update("is_featured", e.target.checked)}
                  className="w-4 h-4 accent-[var(--gold-500)]"
                />
                <span className="inline-flex items-center gap-1.5 text-sm text-navy">
                  <Star size={14} className="text-gold" /> Featured (homepage)
                </span>
              </label>
            </div>

            {/* Featured image */}
            <div className="rounded-xl border border-cream-200 p-5 bg-cream-50">
              <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">Featured Image</div>
              <ImageField
                value={form.featured_image_url}
                onChange={(url) => update("featured_image_url", url)}
                token={token}
                label="Image"
                testid="editor-image"
              />
            </div>

            {/* Categories */}
            <div className="rounded-xl border border-cream-200 p-5 bg-cream-50">
              <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">Categories</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {categories.map((c) => (
                  <label key={c.slug} className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.categories.includes(c.slug)}
                      onChange={() => toggleArr("categories", c.slug)}
                      className="w-4 h-4 accent-[var(--gold-500)]"
                    />
                    {decodeEntities(c.name)}
                  </label>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="rounded-xl border border-cream-200 p-5 bg-cream-50">
              <div className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">Tags</div>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => {
                  const active = form.tags.includes(t.slug);
                  return (
                    <button
                      type="button"
                      key={t.slug}
                      onClick={() => toggleArr("tags", t.slug)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        active
                          ? "bg-navy-deep text-cream border-navy-deep"
                          : "bg-white text-navy border-cream-200 hover:border-gold"
                      }`}
                    >
                      {decodeEntities(t.name)}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* FOOTER ACTIONS */}
          <div className="lg:col-span-3 border-t border-cream-200 pt-5 flex items-center justify-between flex-wrap gap-3">
            {!isNew && (
              <div>
                {confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-700">Delete this devotional?</span>
                    <button type="button" onClick={del} disabled={deleting} data-testid="editor-delete-confirm" className="px-3 py-1.5 rounded-full bg-red-600 text-white text-sm hover:bg-red-700">
                      Yes, delete
                    </button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 rounded-full bg-cream-100 text-navy text-sm">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    data-testid="editor-delete"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-300 text-red-700 hover:bg-red-50 text-sm"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-3 ml-auto">
              {error && (
                <span data-testid="editor-error" className="text-red-700 text-sm inline-flex items-center gap-1.5">
                  <AlertCircle size={14} /> {error}
                </span>
              )}
              <button type="button" onClick={onClose} className="btn-outline-navy px-5 py-2.5 rounded-full text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="editor-save" className="btn-primary px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60">
                <Save size={14} /> {saving ? "Saving..." : (isNew ? "Create" : "Save changes")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-navy mb-1.5">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-navy/55">{hint}</p>}
    </div>
  );
}
